import React from 'react';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

export const HeroNew = () => {
  const navigate = useNavigate();

  const stats = [
    { value: '25+', label: 'Years Experience' },
    { value: '500+', label: 'Providers Served' },
    { value: '99%', label: 'Client Satisfaction' }
  ];

  return (
    <section className="relative min-h-[85vh] flex items-center pt-28 lg:pt-32 pb-24 lg:pb-44 px-4 sm:px-6 lg:px-8 overflow-hidden">
      {/* Background with gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-navy-700 via-navy-600 to-teal-700" />
      
      {/* Animated gradient orbs */}
      <div className="absolute top-20 right-1/4 w-[500px] h-[500px] bg-teal-400/15 rounded-full blur-3xl" />
      <div className="absolute bottom-10 left-1/4 w-[400px] h-[400px] bg-teal-500/10 rounded-full blur-3xl" />
      
      {/* Dotted pattern overlay */}
      <div 
        className="absolute inset-0 opacity-[0.15]"
        style={{
          backgroundImage: `radial-gradient(circle, #40E0D0 1px, transparent 1px)`,
          backgroundSize: '32px 32px'
        }}
      />

      {/* Wave decoration at bottom */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path d="M0 120L48 105C96 90 192 60 288 45C384 30 480 30 576 37.5C672 45 768 60 864 67.5C960 75 1056 75 1152 67.5C1248 60 1344 45 1392 37.5L1440 30V120H1392C1344 120 1248 120 1152 120C1056 120 960 120 864 120C768 120 672 120 576 120C480 120 384 120 288 120C192 120 96 120 48 120H0Z" fill="white"/>
        </svg>
      </div>

      <div className="max-w-7xl mx-auto relative z-10 w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div>
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2 rounded-full mb-6">
              <span className="w-2 h-2 bg-teal-400 rounded-full animate-pulse" />
              <span className="text-teal-300 font-medium text-sm">Medical Billing & Credentialing Experts</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-4 whitespace-nowrap">
              Capital Billing Services
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-teal-400 font-medium italic mb-6">
              Committed to your success, built on our values.
            </p>
            
            {/* Subheadline */}
            <p className="text-lg sm:text-xl text-gray-200 leading-relaxed mb-8 max-w-xl">
              A family owned full service management billing company dedicated to meeting the needs of your practice and offering cost-effective solutions.
            </p>

            {/* Key Value Props */}
            <div className="flex flex-wrap gap-4 mb-8">
              {['Medical Billing', 'Credentialing', 'Consulting'].map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 text-gray-200">
                  <CheckCircle className="w-5 h-5 text-teal-400" />
                  <span className="text-sm font-medium">{item}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <Button
                onClick={() => navigate('/contact')}
                size="lg"
                className="bg-teal-500 hover:bg-teal-400 text-white text-base px-8 py-6 shadow-lg shadow-teal-500/25 hover:shadow-teal-400/30 transition-all duration-300 group rounded-full"
              >
                Get Started Today
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                onClick={() => navigate('/credentialing')}
                size="lg"
                variant="outline"
                className="border-2 border-white/30 bg-white/5 backdrop-blur-sm hover:bg-white/10 text-white text-base px-8 py-6 transition-all duration-300 rounded-full"
              >
                Our Credentialing Process
              </Button>
            </div>

            {/* Phone */}
            <a
              href="tel:4709449277"
              className="inline-flex items-center gap-2 text-gray-300 hover:text-teal-400 transition-colors text-sm"
            >
              <span>Call us:</span>
              <span className="font-bold text-lg text-white">470-944-9277</span>
            </a>
          </div>

          {/* Right Content - Stats Cards */}
          <div className="hidden lg:flex justify-end">
            <div className="grid grid-cols-1 gap-4 w-64">
              {stats.map((stat, idx) => (
                <div
                  key={idx}
                  className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 text-center hover:bg-white/15 hover:border-teal-400/30 transition-all duration-300 group"
                >
                  <div className="text-4xl font-bold text-teal-400 mb-1 group-hover:scale-110 transition-transform duration-300">
                    {stat.value}
                  </div>
                  <div className="text-gray-300 text-sm font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile Stats */}
        <div className="lg:hidden grid grid-cols-3 gap-3 mt-10">
          {stats.map((stat, idx) => (
            <div
              key={idx}
              className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-4 text-center"
            >
              <div className="text-2xl font-bold text-teal-400">{stat.value}</div>
              <div className="text-gray-300 text-xs">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
